import check from "./util/check";
import extend from "./util/extend";
import values from "./util/values";
const { shaderError, linkError, command: _command } = check;

const GL_FRAGMENT_SHADER = 35632;
const GL_VERTEX_SHADER = 35633;

const GL_ACTIVE_UNIFORMS = 0x8b86;
const GL_ACTIVE_ATTRIBUTES = 0x8b89;

export default function wrapShaderState(gl, stringStore, stats, config) {
  // ===================================================
  // glsl compilation and linking
  // ===================================================
  let fragShaders = {};
  let vertShaders = {};

  function ActiveInfo(name, id, location, info) {
    this.name = name;
    this.id = id;
    this.location = location;
    this.info = info;
  }

  function insertActiveInfo(list, info) {
    for (let i = 0; i < list.length; ++i) {
      if (list[i].id === info.id) {
        list[i].location = info.location;
        return;
      }
    }
    list.push(info);
  }

  function getShader(type, id, command) {
    const cache = type === GL_FRAGMENT_SHADER ? fragShaders : vertShaders;
    let shader = cache[id];

    if (!shader) {
      const source = stringStore.str(id);
      shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      shaderError(gl, shader, source, type, command);
      cache[id] = shader;
    }

    return shader;
  }

  // ===================================================
  // program linking
  // ===================================================
  let programCache = {};
  const programList = [];

  let PROGRAM_COUNTER = 0;

  function REGLProgram(fragId, vertId) {
    this.id = PROGRAM_COUNTER++;
    this.fragId = fragId;
    this.vertId = vertId;
    this.program = null;
    this.uniforms = [];
    this.attributes = [];
    this.refCount = 1;

    if (config.profile) {
      this.stats = {
        uniformsCount: 0,
        attributesCount: 0,
      };
    }
  }

  function linkProgram(desc, command, attributeLocations) {
    let i, info;

    // -------------------------------
    // compile & link
    // -------------------------------
    const fragShader = getShader(GL_FRAGMENT_SHADER, desc.fragId);
    const vertShader = getShader(GL_VERTEX_SHADER, desc.vertId);

    const program = (desc.program = gl.createProgram());
    gl.attachShader(program, fragShader);
    gl.attachShader(program, vertShader);
    if (attributeLocations) {
      for (i = 0; i < attributeLocations.length; ++i) {
        const binding = attributeLocations[i];
        gl.bindAttribLocation(program, binding[0], binding[1]);
      }
    }

    gl.linkProgram(program);
    linkError(
      gl,
      program,
      stringStore.str(desc.fragId),
      stringStore.str(desc.vertId),
      command
    );

    // -------------------------------
    // grab uniforms
    // -------------------------------
    const numUniforms = gl.getProgramParameter(program, GL_ACTIVE_UNIFORMS);
    if (config.profile) {
      desc.stats.uniformsCount = numUniforms;
    }
    const uniforms = desc.uniforms;
    for (i = 0; i < numUniforms; ++i) {
      info = gl.getActiveUniform(program, i);
      if (info) {
        if (info.size > 1) {
          for (let j = 0; j < info.size; ++j) {
            const name = info.name.replace("[0]", "[" + j + "]");
            insertActiveInfo(
              uniforms,
              new ActiveInfo(
                name,
                stringStore.id(name),
                gl.getUniformLocation(program, name),
                info
              )
            );
          }
        } else {
          insertActiveInfo(
            uniforms,
            new ActiveInfo(
              info.name,
              stringStore.id(info.name),
              gl.getUniformLocation(program, info.name),
              info
            )
          );
        }
      }
    }

    // -------------------------------
    // grab attributes
    // -------------------------------
    const numAttributes = gl.getProgramParameter(program, GL_ACTIVE_ATTRIBUTES);
    if (config.profile) {
      desc.stats.attributesCount = numAttributes;
    }

    const attributes = desc.attributes;
    for (i = 0; i < numAttributes; ++i) {
      info = gl.getActiveAttrib(program, i);
      if (info) {
        insertActiveInfo(
          attributes,
          new ActiveInfo(
            info.name,
            stringStore.id(info.name),
            gl.getAttribLocation(program, info.name),
            info
          )
        );
      }
    }
  }

  if (config.profile) {
    stats.getMaxUniformsCount = function() {
      let m = 0;
      programList.forEach(function(desc) {
        if (desc.stats.uniformsCount > m) {
          m = desc.stats.uniformsCount;
        }
      });
      return m;
    };

    stats.getMaxAttributesCount = function() {
      let m = 0;
      programList.forEach(function(desc) {
        if (desc.stats.attributesCount > m) {
          m = desc.stats.attributesCount;
        }
      });
      return m;
    };
  }

  function restoreShaders() {
    fragShaders = {};
    vertShaders = {};
    for (let i = 0; i < programList.length; ++i) {
      linkProgram(
        programList[i],
        null,
        programList[i].attributes.map(function(info) {
          return [info.location, info.name];
        })
      );
    }
  }

  return {
    clear: function() {
      const deleteShader = gl.deleteShader.bind(gl);
      values(fragShaders).forEach(deleteShader);
      fragShaders = {};
      values(vertShaders).forEach(deleteShader);
      vertShaders = {};

      programList.forEach(function(desc) {
        gl.deleteProgram(desc.program);
      });
      programList.length = 0;
      programCache = {};

      stats.shaderCount = 0;
    },

    program: function(vertId, fragId, command, attribLocations) {
      _command(vertId >= 0, "missing vertex shader", command);
      _command(fragId >= 0, "missing fragment shader", command);

      let cache = programCache[fragId];
      if (!cache) {
        cache = programCache[fragId] = {};
      }
      const prevProgram = cache[vertId];
      if (prevProgram) {
        prevProgram.refCount++;
        if (!attribLocations) {
          return prevProgram;
        }
      }
      const program = new REGLProgram(fragId, vertId);
      stats.shaderCount++;
      linkProgram(program, command, attribLocations);
      if (!prevProgram) {
        cache[vertId] = program;
      }
      programList.push(program);
      return extend(program, {
        destroy: function() {
          program.refCount--;
          if (program.refCount <= 0) {
            gl.deleteProgram(program.program);
            const idx = programList.indexOf(program);
            programList.splice(idx, 1);
            stats.shaderCount--;
          }
          // no program is linked to this vert anymore
          if (cache[program.vertId].refCount <= 0) {
            gl.deleteShader(vertShaders[program.vertId]);
            delete vertShaders[program.vertId];
            delete programCache[program.fragId][program.vertId];
          }
          // no program is linked to this frag anymore
          if (!Object.keys(programCache[program.fragId]).length) {
            gl.deleteShader(fragShaders[program.fragId]);
            delete fragShaders[program.fragId];
            delete programCache[program.fragId];
          }
        },
      });
    },

    restore: restoreShaders,

    shader: getShader,

    frag: -1,
    vert: -1,
  };
}
